import React, { memo, useContext } from 'react';
import type { NodeProps } from 'reactflow';
import type { Cluster, ClusterVocab } from '../../types/cluster';
import { ClusterStatus, CLUSTER_STATUS_CSS_VARS } from '../../types/cluster';
import TierLanes, { type TierBand } from './TierLanes';
import { SelectedClusterContext } from '../shared/SelectedClusterContext';
import { formatCount } from '../../utils/plural';

export interface ClusterRegionNodeData {
  cluster: Cluster | null;
  vocab: ClusterVocab;
  memberCount: number;
  parentName?: string | null;
  /**
   * Optional accent color (CSS color string, including `var(--token)`).
   * Build view sets this from the dominant node-category of cluster
   * members and gets the new visual treatment (4px top accent bar,
   * 10% wash header, taller `h-11` header). When omitted (e.g.
   * research view, which has no node-category concept), the V1.6
   * status-driven treatment is preserved exactly — no top bar, `h-9`
   * header, status-tinted wash with the original `headerTint` rule.
   */
  accentColor?: string;
  /**
   * Per-tier background bands (interior coordinates) for the zebra tier
   * shading + corner numbers. Populated by the tiered layout in both build
   * and research; absent for flow layout, which renders no lanes. Rendered
   * the same way in both visual treatments (build accent + research V1.6).
   */
  tierBands?: TierBand[];
  /**
   * Which slice of the cluster box this node renders. Build view splits the box
   * into a `body` node (border + fill + zebra, BELOW the edges) and a `header`
   * child (title bar, ABOVE the edges) so the connector lines run between them
   * without the zebra fading them. Undefined = render the whole box combined
   * (research view, and the default fallback).
   */
  layer?: 'body' | 'header';
  /**
   * Id used for cluster-select highlight/dim when it differs from the node's
   * own id — the build-view `header` child carries its parent region's id here
   * so selecting the box still tracks the whole cluster. Defaults to the node id.
   */
  selectionId?: string;
}

function clusterStatusLabel(status: ClusterStatus): string {
  return status.replace(/_/g, ' ');
}

function ClusterRegionNode({ id, data }: NodeProps<ClusterRegionNodeData>) {
  const {
    cluster,
    vocab,
    memberCount,
    parentName,
    accentColor,
    tierBands,
    layer,
  } = data;

  const status = cluster?.status ?? ClusterStatus.DRAFT;
  const statusVar = CLUSTER_STATUS_CSS_VARS[status];

  // Cluster-select highlight: the clicked region gets an accent ring; while any
  // cluster is selected, the other region boxes dim to focus attention. The
  // split build header child carries its parent region's id in `selectionId`
  // so highlight/dim tracks the whole box, not the child's suffixed node id.
  const selectionId = data.selectionId ?? id;
  const selectedClusterId = useContext(SelectedClusterContext);
  const isActive = selectedClusterId === selectionId;
  const isDimmed = selectedClusterId !== null && !isActive;
  // Ring tracks the box's own accent — category color in build, status color in
  // research — so it reads as "this cluster" rather than a generic selection.
  const ringColor = accentColor ?? `var(${statusVar})`;
  const activeRingShadow = `0 0 0 3px color-mix(in srgb, ${ringColor} 35%, transparent), var(--shadow-md)`;

  const titleText = cluster?.name ?? 'Untitled cluster';
  const memberText = formatCount(memberCount, { one: vocab.singular, other: vocab.plural });
  const detailText = `${vocab.singular} · ${memberText}${parentName ? ` · parent: ${parentName}` : ''}`;
  const statusLabel = clusterStatusLabel(status);
  const headerText = `${titleText} · ${detailText} · ${statusLabel}`;

  // Build-view treatment (accentColor present): 2px accent bar + opaque 10%
  // accent header + compact 26px header + filled-dot status indicator. Build
  // view splits the box into a `body` node and a `header` child (see `layer`)
  // so the edges run between the zebra and the title; the combined render (no
  // layer) is the default fallback.
  if (accentColor) {
    // OPAQUE header: a 10% accent tint over the solid --surface base (not over
    // `transparent`), so the header strip occludes the connector lines behind it
    // exactly like the opaque node cards do.
    const headerBackground = `color-mix(in srgb, ${accentColor} 10%, var(--surface))`;
    const headerBorder = `color-mix(in srgb, ${accentColor} 35%, transparent)`;
    const regionBorder = `color-mix(in srgb, ${accentColor} 22%, transparent)`;

    // The title bar: 2px accent rail + opaque title/status/member row. Shared by
    // the combined render and the standalone `header` child.
    const headerStrip = (
      <>
        {/* 2px colored top accent bar — dominant node category. */}
        <div
          aria-hidden
          className="relative h-[2px] w-full"
          style={{ background: accentColor }}
        />
        <div
          className="relative h-[26px] px-[10px] flex items-center gap-2 border-b"
          style={{
            background: headerBackground,
            borderBottomColor: headerBorder,
          }}
          title={cluster?.summary || headerText}
        >
          <div className="min-w-0 flex-1 truncate text-[11px] leading-none font-semibold uppercase tracking-[0.06em] text-fg">
            {titleText}
          </div>
          <div className="flex flex-shrink-0 items-center gap-2 truncate text-[9px] leading-none font-medium uppercase tracking-[0.04em] text-fg-secondary">
            <span
              className="inline-flex items-center gap-1"
              title={`Status: ${statusLabel}`}
            >
              <span
                aria-hidden
                className="h-1.5 w-1.5 rounded-full"
                style={{ background: `var(${statusVar})` }}
              />
              <span>{statusLabel}</span>
            </span>
            <span>{memberText}</span>
          </div>
          {parentName && (
            <span className="max-w-[120px] flex-shrink truncate text-[10px] leading-none uppercase tracking-[0.04em] text-fg-secondary">
              {parentName}
            </span>
          )}
        </div>
      </>
    );

    // `header` layer: the title bar on its own, pinned ABOVE the edge layer so it
    // occludes the lines. Rounded top corners match the body box. The node is
    // pointer-events:none (set on its React Flow style) so clicks fall through to
    // the body and cluster-select keeps working.
    if (layer === 'header') {
      return (
        <div
          className="cluster-region-header relative overflow-hidden rounded-t-lg"
          style={{
            width: '100%',
            height: '100%',
            opacity: isDimmed ? 0.3 : 1,
            transition: 'opacity 0.2s ease',
          }}
          aria-label={headerText}
        >
          {headerStrip}
        </div>
      );
    }

    // `body` layer (and the combined fallback): border + fill + zebra. As the
    // split BODY node it sits BELOW the edges, so the zebra never fades the
    // connector lines; the header rides above as a separate child. The combined
    // render (no layer) additionally draws the header inline.
    return (
      <section
        className="cluster-region-node relative overflow-hidden rounded-lg border"
        style={{
          width: '100%',
          height: '100%',
          borderColor: isActive ? accentColor : regionBorder,
          background: 'color-mix(in srgb, var(--surface) 18%, transparent)',
          boxShadow: isActive ? activeRingShadow : 'var(--shadow-xs)',
          opacity: isDimmed ? 0.3 : 1,
          transition: 'opacity 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
        }}
        aria-label={headerText}
      >
        {/* Alternating full-row tier shading (zebra) behind the content. */}
        <TierLanes bands={tierBands ?? []} />
        {layer === 'body' ? null : headerStrip}
      </section>
    );
  }

  // V1.6 treatment (no accentColor): status-driven wash, 26px header, bullet
  // status indicator, APPROVED-only inset side bar. Split into body + header the
  // same way as build (see `layer`) so connector edges run between the zebra and
  // the title; the combined render (no layer) is the fallback.
  const headerTint = status === ClusterStatus.APPROVED ? 28 : 18;
  const borderColor = `color-mix(in srgb, var(${statusVar}) 60%, transparent)`;
  // OPAQUE header: mix the status tint over solid --surface (not transparent) so
  // the header strip occludes connector lines passing behind it, like the cards.
  const headerBackground = `color-mix(in srgb, var(${statusVar}) ${headerTint}%, var(--surface))`;

  const headerStrip = (
    <div
      className="relative h-[26px] px-[10px] flex items-center gap-2 border-b"
      style={{
        background: headerBackground,
        borderBottomColor: borderColor,
        boxShadow: status === ClusterStatus.APPROVED
          ? `inset 3px 0 0 var(${statusVar})`
          : undefined,
      }}
      title={cluster?.summary || headerText}
    >
      <div className="min-w-0 flex-1 truncate text-[11px] leading-none font-semibold uppercase tracking-[0.04em] text-fg">
        {titleText}
      </div>
      <div className="flex flex-shrink-0 items-center gap-2 truncate text-[10px] leading-none font-medium uppercase tracking-[0.04em] text-fg-secondary">
        <span className="inline-flex items-center gap-1">
          <span aria-hidden style={{ color: `var(${statusVar})` }}>●</span>
          <span>{statusLabel}</span>
        </span>
        <span>{memberText}</span>
      </div>
      {parentName && (
        <span className="max-w-[120px] flex-shrink truncate text-[10px] leading-none uppercase tracking-[0.04em] text-fg-secondary">
          {parentName}
        </span>
      )}
    </div>
  );

  // `header` layer: the title bar alone, pinned ABOVE the edge layer so it
  // occludes the lines. pointer-events:none (set on the RF node style) so clicks
  // fall through to the body and cluster-select keeps working.
  if (layer === 'header') {
    return (
      <div
        className="cluster-region-header relative overflow-hidden rounded-t-md"
        style={{ width: '100%', height: '100%', opacity: isDimmed ? 0.3 : 1, transition: 'opacity 0.2s ease' }}
        aria-label={headerText}
      >
        {headerStrip}
      </div>
    );
  }

  // `body` layer (and combined fallback): border + fill + zebra, BELOW the edges.
  return (
    <section
      className="cluster-region-node relative overflow-hidden rounded-md border"
      style={{
        width: '100%',
        height: '100%',
        borderColor: isActive ? `var(${statusVar})` : borderColor,
        background: 'color-mix(in srgb, var(--surface) 18%, transparent)',
        boxShadow: isActive ? activeRingShadow : 'var(--shadow-xs)',
        opacity: isDimmed ? 0.3 : 1,
        transition: 'opacity 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
      }}
      aria-label={headerText}
    >
      {/* Alternating full-row tier shading (zebra) + corner numbers. */}
      <TierLanes bands={tierBands ?? []} />
      {layer === 'body' ? null : headerStrip}
    </section>
  );
}

export default memo(ClusterRegionNode);
